import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { API_BASE_URL } from "../../../../../../environment";

interface TenantSubData {
  tenant_subscription_counts: {
    [year: string]: {
      [month: string]: number;
    };
  };
  summary: {
    total_subscriptions: number;
    active_subscriptions: number;
    inactive_subscriptions: number;
  };
}

const TenantSubTrend = () => {
  const [data, setData] = useState<TenantSubData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const authHeader = localStorage.getItem("authCred") || "";
        const response = await fetch(
          `${API_BASE_URL}/dashboard/tenant-subscription-stats-by-month/`,
          {
            headers: {
              Authorization: `Basic ${authHeader}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        setData(result);
      } catch (error) {
        console.error("Error fetching tenant subscription data:", error);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getChartData = () => {
    if (!data || !data.tenant_subscription_counts) {
      return { categories: [], chartData: [] };
    }

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const chartData = [];
    const categories = [];

    for (const [year, months] of Object.entries(
      data.tenant_subscription_counts
    )) {
      for (const [month, count] of Object.entries(months)) {
        const monthIndex = Object.keys(months).indexOf(month);
        categories.push(`${monthNames[monthIndex]} ${year}`);
        chartData.push(count);
      }
    }

    return { categories, chartData };
  };

  const { categories, chartData } = getChartData();
  const maxValue = chartData.length > 0 ? Math.max(...chartData) : 10;

  const options: ApexOptions = {
    chart: {
      type: "area" as const,
      height: 273,
      zoom: { enabled: false },
      toolbar: { show: false },
    },
    colors: ["#FFA201"],
    dataLabels: { enabled: false },
    stroke: { curve: "straight" },
    fill: {
      type: "solid",
      opacity: 0,
    },
    markers: {
      size: 5,
      shape: "circle",
      strokeWidth: 2,
      strokeColors: "#FFA201",
      hover: {
        size: 7,
      },
    },
    grid: {
      borderColor: "#E8E8E8",
      strokeDashArray: 4,
    },
    xaxis: {
      categories:
        categories.length > 0
          ? categories
          : [
              "Jan",
              "Feb",
              "Mar",
              "Apr",
              "May",
              "Jun",
              "Jul",
              "Aug",
              "Sep",
              "Oct",
              "Nov",
              "Dec",
            ],
    },
    yaxis: {
      min: 0,
      max: maxValue > 0 ? Math.ceil(maxValue * 1.2) : 10,
      forceNiceScale: true,
      labels: {
        offsetX: -15,
        formatter: (val: number) => Math.round(val).toString(),
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "left",
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${val} subscriptions`,
      },
    },
  };

  const series = [
    {
      name: "Subscriptions",
      data:
        chartData.length > 0
          ? chartData
          : [1, 2, 3, 1.5, 2.2, 4, 3.0, 2.0, 3.0, 1.8, 3.0, 6.0],
    },
  ];

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 273 }}
      >
        <div className="text-center">
          <div className="spinner-border text-primary mb-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading chart data...</p>
        </div>
      </div>
    );
  }

  if (!data || Object.keys(data.tenant_subscription_counts).length === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 273 }}
      >
        <div className="text-center">
          <i
            className="ti ti-chart-line text-muted mb-3"
            style={{ fontSize: "3rem" }}
          ></i>
          <h5 className="text-muted">No Data Available</h5>
          <p className="text-muted">No subscription trend data found</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Chart options={options} series={series} type="area" height={273} />
    </>
  );
};

export default TenantSubTrend;
